import { gql, useMutation } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ButtonSpinner from '../../components/button-spinner';
import MainContentCard from '../../components/layout/main-layout/main-content-card';
import Spinner from '../../components/spinner';
import Typography from '../../components/typography';
import useSearchParam from '../../hooks/use-search-param';
import { useTranslation } from '../../hooks/use-translation';
import HdGuide, { HOME_DEPOT_SKILL_KEY } from './hd-guide';

type QuizQuestion = {
  id: string;
  prompt: string;
  choices: Record<string, string>;
};

type QuizAttempt = {
  id: string;
  skillKey: string;
  questions: QuizQuestion[];
};

// Each certification's briefing is opt-in by skill_key, so a certification
// with no guide here just skips straight to its quiz instead of showing the
// Home Depot content.
const GUIDES_BY_SKILL_KEY: Record<
  string,
  ComponentType<{ onUnlock: () => void }>
> = {
  [HOME_DEPOT_SKILL_KEY]: HdGuide,
};

type QuizAnswerResult = {
  quizQuestionId: string;
  prompt: string;
  choices: Record<string, string>;
  selectedChoiceKey: string;
  correctChoiceKey: string;
  correct: boolean;
};

type QuizAttemptResult = {
  passed: boolean;
  score: number | null;
  answers: QuizAnswerResult[];
};

type StartQuizAttemptData = { startQuizAttempt: QuizAttempt };
type StartQuizAttemptVariables = { token?: string; skillKey?: string };

type SubmitQuizAttemptData = { submitQuizAttempt: QuizAttemptResult };
type SubmitQuizAttemptVariables = {
  quizAttemptId: string;
  answers: { quizQuestionId: string; selectedChoiceKey: string }[];
};

export const START_QUIZ_ATTEMPT_MUTATION = gql`
  mutation StartQuizAttempt($token: String, $skillKey: String) {
    startQuizAttempt(input: { token: $token, skillKey: $skillKey }) {
      id
      skillKey
      questions {
        id
        prompt
        choices
      }
    }
  }
`;

export const SUBMIT_QUIZ_ATTEMPT_MUTATION = gql`
  mutation SubmitQuizAttempt(
    $quizAttemptId: ID!
    $answers: [QuizAnswerInput!]!
  ) {
    submitQuizAttempt(
      input: { quizAttemptId: $quizAttemptId, answers: $answers }
    ) {
      passed
      score
      answers {
        quizQuestionId
        prompt
        choices
        selectedChoiceKey
        correctChoiceKey
        correct
      }
    }
  }
`;

type Phase =
  | 'loading'
  | 'no_link'
  | 'invalid'
  | 'expired'
  | 'already_certified'
  | 'not_eligible'
  | 'no_active_quiz'
  | 'quiz'
  | 'passed'
  | 'failed';

function errorCodeToPhase(code: unknown): Phase {
  switch (code) {
    case 'already_certified':
      return 'already_certified';
    case 'invitation_expired':
      return 'expired';
    // not_enabled reuses the "not an approved driver" copy below - both
    // cases boil down to "you can't take this quiz right now," and
    // Quizzes::Rollout being off is not yet a driver-facing distinction
    // worth its own screen.
    case 'not_eligible':
    case 'not_enabled':
      return 'not_eligible';
    case 'no_active_quiz':
      return 'no_active_quiz';
    default:
      return 'invalid';
  }
}

// A lapsed session surfaces as a network error with no extensions.code,
// since GraphqlController raises before the resolver runs - shared by
// beginAttempt and onSubmit since a session can lapse during either the
// guide or the quiz itself, both of which can take a while to get through.
function redirectToSignInIfSessionLapsed(err: unknown): boolean {
  if (!(err as { networkError?: unknown })?.networkError) return false;

  const target = `${window.location.pathname}${window.location.search}`;
  window.location.href = `/sign-in?redirectUrl=${encodeURIComponent(target)}`;
  return true;
}

function QuizPage(): JSX.Element {
  const { t } = useTranslation('quiz');
  const [token] = useSearchParam<string>({ key: 'token' });
  const [skill] = useSearchParam<string>({ key: 'skill' });

  const [phase, setPhase] = useState<Phase>(
    token || skill ? 'loading' : 'no_link',
  );
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [result, setResult] = useState<QuizAttemptResult | null>(null);
  const [selectedChoices, setSelectedChoices] = useState<
    Record<string, string>
  >({});
  const [showValidationError, setShowValidationError] = useState(false);
  const [guideAcknowledged, setGuideAcknowledged] = useState(false);

  const [startQuizAttempt] = useMutation<
    StartQuizAttemptData,
    StartQuizAttemptVariables
  >(START_QUIZ_ATTEMPT_MUTATION);
  const [submitQuizAttempt, { loading: submitting }] = useMutation<
    SubmitQuizAttemptData,
    SubmitQuizAttemptVariables
  >(SUBMIT_QUIZ_ATTEMPT_MUTATION);

  const beginAttempt = useCallback(() => {
    if (!token && !skill) return;

    setPhase('loading');
    setResult(null);
    setSelectedChoices({});
    setShowValidationError(false);

    // Sent as exactly one key, not `{ token, skillKey: undefined }` - an
    // explicit undefined still shows up as a variable to Apollo's mock
    // matching in tests, even though the wire request drops it.
    const variables = token ? { token } : { skillKey: skill as string };

    startQuizAttempt({ variables })
      .then(({ data }) => {
        if (!data) return;
        setAttempt(data.startQuizAttempt);
        setPhase('quiz');
      })
      .catch(err => {
        const code = err?.graphQLErrors?.[0]?.extensions?.code;

        if (code) {
          setPhase(errorCodeToPhase(code));
          return;
        }

        if (redirectToSignInIfSessionLapsed(err)) return;

        setPhase('invalid');
      });
  }, [token, skill, startQuizAttempt]);

  useEffect(() => {
    beginAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, skill]);

  const allQuestionsAnswered = useMemo(
    () =>
      !!attempt &&
      attempt.questions.every(question => !!selectedChoices[question.id]),
    [attempt, selectedChoices],
  );

  const onRetake = () => {
    // A retake goes back through the guide too, not straight to a fresh
    // attempt - settled behavior, not an oversight.
    setGuideAcknowledged(false);
    beginAttempt();
  };

  const onSubmit = () => {
    if (!attempt) return;

    if (!allQuestionsAnswered) {
      setShowValidationError(true);
      return;
    }

    const answers = attempt.questions.map(question => ({
      quizQuestionId: question.id,
      selectedChoiceKey: selectedChoices[question.id],
    }));

    submitQuizAttempt({ variables: { quizAttemptId: attempt.id, answers } })
      .then(({ data }) => {
        if (!data) return;
        setResult(data.submitQuizAttempt);
        setPhase(data.submitQuizAttempt.passed ? 'passed' : 'failed');
      })
      .catch(err => {
        const code = err?.graphQLErrors?.[0]?.extensions?.code;

        if (code) {
          setPhase(errorCodeToPhase(code));
          return;
        }

        if (redirectToSignInIfSessionLapsed(err)) return;

        enqueueSnackbar(t('SUBMIT_ERROR'), { variant: 'error' });
      });
  };

  if (phase === 'no_link' || phase === 'invalid') {
    return (
      <MainContentCard
        title={t('LINK_INVALID_TITLE')}
        content={<Typography size="sm">{t('LINK_INVALID_DESC')}</Typography>}
      />
    );
  }

  if (phase === 'no_active_quiz') {
    return (
      <MainContentCard
        title={t('NO_ACTIVE_QUIZ_TITLE')}
        content={<Typography size="sm">{t('NO_ACTIVE_QUIZ_DESC')}</Typography>}
      />
    );
  }

  if (phase === 'expired') {
    return (
      <MainContentCard
        title={t('EXPIRED_TITLE')}
        content={<Typography size="sm">{t('EXPIRED_DESC')}</Typography>}
      />
    );
  }

  if (phase === 'already_certified' || phase === 'passed') {
    const titleKey =
      phase === 'passed' ? 'PASSED_TITLE' : 'ALREADY_CERTIFIED_TITLE';

    return (
      <MainContentCard
        title={t(titleKey)}
        content={
          <Typography size="sm">
            {t(phase === 'passed' ? 'PASSED_DESC' : 'ALREADY_CERTIFIED_DESC')}
          </Typography>
        }
      />
    );
  }

  if (phase === 'not_eligible') {
    return (
      <MainContentCard
        title={t('NOT_ELIGIBLE_TITLE')}
        content={<Typography size="sm">{t('NOT_ELIGIBLE_DESC')}</Typography>}
      />
    );
  }

  if (phase === 'failed' && result) {
    return (
      <MainContentCard
        title={t('FAILED_TITLE')}
        content={
          <div className="space-y-4">
            <Typography size="sm">{t('FAILED_DESC')}</Typography>
            <div className="space-y-4">
              {result.answers
                .filter(answer => !answer.correct)
                .map(answer => (
                  <div
                    key={answer.quizQuestionId}
                    className="border rounded-md p-3 space-y-1"
                  >
                    <Typography size="sm" fontWeight="bold">
                      {answer.prompt}
                    </Typography>
                    <Typography size="sm" color="destructive">
                      {`${t('YOUR_ANSWER')}: ${answer.selectedChoiceKey}`}
                    </Typography>
                    <Typography size="sm" color="gray-600">
                      {`${t('CORRECT_ANSWER')}: ${answer.correctChoiceKey} - ${answer.choices[answer.correctChoiceKey]}`}
                    </Typography>
                  </div>
                ))}
            </div>
          </div>
        }
        footer={
          <ButtonSpinner
            type="button"
            data-testid="retake-button"
            fullWidth
            onClick={onRetake}
          >
            {t('RETAKE')}
          </ButtonSpinner>
        }
      />
    );
  }

  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center mt-5">
        <Spinner />
      </div>
    );
  }

  const Guide = attempt ? GUIDES_BY_SKILL_KEY[attempt.skillKey] : undefined;

  if (!guideAcknowledged && phase === 'quiz' && Guide) {
    return <Guide onUnlock={() => setGuideAcknowledged(true)} />;
  }

  if (phase === 'quiz' && attempt) {
    return (
      <MainContentCard
        title={t('PAGE_TITLE')}
        content={
          <div className="space-y-6">
            {attempt.questions.map((question, index) => (
              <fieldset key={question.id} className="space-y-2">
                <legend>
                  <Typography size="sm" fontWeight="bold">
                    {t('QUESTION_LABEL', {
                      replace: {
                        current: String(index + 1),
                        total: String(attempt.questions.length),
                      },
                    })}
                  </Typography>
                  <Typography size="sm">{question.prompt}</Typography>
                </legend>
                <div className="space-y-1">
                  {Object.entries(question.choices).map(
                    ([choiceKey, choiceText]) => {
                      const inputId = `${question.id}-${choiceKey}`;

                      return (
                        <label
                          key={choiceKey}
                          htmlFor={inputId}
                          className="flex items-center space-x-2"
                        >
                          <input
                            id={inputId}
                            type="radio"
                            name={question.id}
                            value={choiceKey}
                            checked={selectedChoices[question.id] === choiceKey}
                            onChange={() =>
                              setSelectedChoices(previous => ({
                                ...previous,
                                [question.id]: choiceKey,
                              }))
                            }
                          />
                          <Typography size="sm">{choiceText}</Typography>
                        </label>
                      );
                    },
                  )}
                </div>
              </fieldset>
            ))}
            {showValidationError && !allQuestionsAnswered && (
              <Typography size="sm" color="destructive">
                {t('ANSWER_ALL_QUESTIONS')}
              </Typography>
            )}
          </div>
        }
        footer={
          <ButtonSpinner
            type="button"
            data-testid="submit-quiz-button"
            fullWidth
            loading={submitting}
            onClick={onSubmit}
          >
            {t('SUBMIT')}
          </ButtonSpinner>
        }
      />
    );
  }

  return (
    <div className="flex items-center justify-center mt-5">
      <Spinner />
    </div>
  );
}

export default QuizPage;
